"use client";

import * as React from "react";
import { DayPicker } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { format, getYear, setMonth, setYear } from "date-fns";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale.ts";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  fromYearSelect: number;
  onMonthChange?: (month: Date) => void;
  language?: string | undefined;
};

function CalendarWithYearSelect({
  className,
  classNames,
  showOutsideDays = true,
  language,
  fromYearSelect,
  ...props
}: CalendarProps) {
  const { t, i18n } = useTranslation();
  const [currentMonth, setCurrentMonth] = React.useState<Date>(props.defaultMonth || new Date());

  const years = React.useMemo(() => {
    return Array.from({ length: 100 }, (_, i) => fromYearSelect - 50 + i);
  }, []);

  const months = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const date = new Date(2021, i, 1);

      return format(date, "MMMM", { locale: getDateFnsLocale(language ?? i18n.language) });
    });
  }, [i18n.language, language]);

  const handleYearChange = (year: string) => {
    const newMonth = setYear(currentMonth, Number.parseInt(year));

    setCurrentMonth(newMonth);
  };

  const handleMonthChange = (monthName: string) => {
    const monthIndex = months.findIndex((m) => m === monthName);
    const newMonth = setMonth(currentMonth, monthIndex);

    setCurrentMonth(newMonth);
  };

  return (
    <DayPicker
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "hidden",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100",
        ),
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside: "text-muted-foreground opacity-50",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        Caption: () => (
          <div className="flex items-center justify-center gap-2">
            <Select value={getYear(currentMonth).toString()} onValueChange={handleYearChange}>
              <SelectTrigger className="w-[100px] bg-background">
                <SelectValue placeholder={t("Select year")} />
              </SelectTrigger>
              <SelectContent className={"z-200 bg-background"}>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={format(currentMonth, "MMMM", { locale: getDateFnsLocale(language ?? "EN") })}
              onValueChange={handleMonthChange}
            >
              <SelectTrigger className="w-[130px] bg-background">
                <p className={"capitalize"}>
                  <SelectValue placeholder={t("Select month")} />
                </p>
              </SelectTrigger>
              <SelectContent className="z-200 bg-background opacity-100">
                {months.map((month) => (
                  <SelectItem key={month} className={"capitalize"} value={month}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ),
      }}
      month={currentMonth}
      showOutsideDays={showOutsideDays}
      {...props}
    />
  );
}

CalendarWithYearSelect.displayName = "CalendarWithYearSelect";

export { CalendarWithYearSelect };
